/* Header predictive search: suggestions from the Predictive Search API, rendered by the
   predictive-search section. A normal GET form underneath, so it still works without JavaScript.
   Wrapped in a block so nothing here leaks into the global scope shared with macsolve.js. */
{
  class MsPredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.panel = this.querySelector('[data-panel]');
      if (!this.input || !this.panel) return;
      this.input.setAttribute('role', 'combobox');
      this.input.setAttribute('aria-autocomplete', 'list');
      this.input.setAttribute('aria-haspopup', 'listbox');
      this.input.setAttribute('aria-controls', 'PredictiveList');
      this.input.setAttribute('aria-expanded', 'false');

      this.input.addEventListener('input', () => {
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.search(), 200);
      });
      this.input.addEventListener('focus', () => {
        if (this.panel.innerHTML.trim() && this.input.value.trim().length >= 2) this.open();
      });
      /* Safari does not focus a link on mousedown, so without this the focusout below would close the
         panel before the click lands. Keeping focus where it is lets the click through. */
      this.panel.addEventListener('mousedown', (event) => event.preventDefault());
      this.addEventListener('keydown', (event) => this.onKey(event));
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.close();
      });
      this.onOutside = (event) => {
        if (!this.contains(event.target)) this.close();
      };
      document.addEventListener('click', this.onOutside);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onOutside);
      clearTimeout(this.timer);
      this.controller?.abort();
    }

    async search() {
      const term = this.input.value.trim();
      if (term.length < 2) {
        this.close();
        return;
      }
      this.controller?.abort();
      this.controller = new AbortController();
      const url =
        `${this.dataset.url}?q=${encodeURIComponent(term)}` +
        '&resources[type]=product,collection,page,article&resources[limit]=5&resources[limit_scope]=each' +
        '&section_id=predictive-search';
      try {
        const response = await fetch(url, { signal: this.controller.signal });
        if (!response.ok) throw new Error(String(response.status));
        const next = new DOMParser().parseFromString(await response.text(), 'text/html');
        const fresh = next.querySelector('[data-predictive-results]');
        if (!fresh) return this.close();
        this.panel.innerHTML = fresh.innerHTML;
        this.open();
      } catch (error) {
        if (error.name !== 'AbortError') this.close();
      }
    }

    open() {
      this.panel.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }

    close() {
      this.panel.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
    }

    /* Arrow keys move through the suggestions, Escape closes and returns to the input */
    onKey(event) {
      if (this.panel.hidden) return;
      const options = [...this.panel.querySelectorAll('[role="option"]')];
      const index = options.indexOf(document.activeElement);
      if (event.key === 'Escape') {
        event.stopPropagation();
        this.close();
        this.input.focus();
      } else if (event.key === 'ArrowDown' && options.length) {
        event.preventDefault();
        options[Math.min(index + 1, options.length - 1)].focus();
      } else if (event.key === 'ArrowUp' && index >= 0) {
        event.preventDefault();
        if (index === 0) this.input.focus();
        else options[index - 1].focus();
      }
    }
  }
  customElements.define('ms-predictive-search', MsPredictiveSearch);
}
